import { DocumentNode, gql } from '@apollo/client';
import {
  Avatar,
  Box,
  Checkbox,
  HStack,
  Hide,
  Td,
  Text,
  Tooltip,
  Tr,
  useBreakpointValue,
  useDisclosure,
} from '@chakra-ui/react';
import dayjs from 'dayjs';
import dynamic from 'next/dynamic';
import NextLink from 'next/link';
import { useRouter } from 'next/router';
import * as React from 'react';

import { FileNameTooltipLabel } from '@/components/FileNameTooltipLabel';
import { DeleteLibraryResourceModal } from '@/components/LibraryResourceModals/DeleteLibraryResourceModal';
import { RenameLibraryResourceModal } from '@/components/LibraryResourceModals/RenameLibraryResourceModal';
import { PreviewRendererProps } from '@/components/PreviewRenderer/PreviewRenderer';
import { PREVIEW_RENDERER_VERSION_FILE_FRAGMENT } from '@/components/PreviewRenderer/PreviewRendererFragment';
import { TruncatedItemName } from '@/components/TruncatedItemName';
import { getFileTypeDisplayInfo } from '@/imports/ui/UtilsNoVulcan';
import { BulkMoveLibraryFilesModal } from '@/imports/ui/components/resource/BulkMoveLibraryFilesModal';
import { ISelectionState } from '@/imports/ui/useSelectionState';

import { ResourceListItem_VersionFileFragment } from './graphql-types/ResourceListItem_VersionFileFragment';

import { FileItemDropdown } from './components/FileItemDropdown';

/* we can't load parts of react-dom-preview on the server  */
const PreviewRendererModal = dynamic<PreviewRendererProps>(
  () =>
    import('@/components/PreviewRenderer/PreviewRenderer').then(
      (mod) => mod.PreviewRenderer
    ),
  {
    ssr: false,
  }
);

type IVersionFile = ResourceListItem_VersionFileFragment;

type IResourceListItemProps = {
  versionFile: IVersionFile | null;
  selection: ISelectionState<{ _id: string }>;
  toggleOne: (item: { _id: string }) => void;
  canEditGroupLibrary: boolean;
  groupId: string;
  rootFolderId: string;
  onAfterDelete: () => void;
  onAfterMove: () => void;
};

export const ResourceListItem: React.FC<IResourceListItemProps> & {
  versionFileFragment: DocumentNode;
} = ({
  versionFile,
  selection,
  toggleOne,
  canEditGroupLibrary,
  groupId,
  rootFolderId,
  onAfterDelete,
  onAfterMove,
}) => {
  const moveFileModal = useDisclosure();
  const deleteFileModal = useDisclosure();
  const renameFileModal = useDisclosure();
  const router = useRouter();

  const lineLength = useBreakpointValue({
    base: 20,
    sm: 40,
    md: 40,
    lg: 18,
    xl: 50,
  });
  const numLines = useBreakpointValue({ base: 1, md: 2 });

  if (!versionFile?.file) return null;

  const { ChakraIcon, previewDisabled } = getFileTypeDisplayInfo(
    versionFile.file.type
  );

  const openFilePreviewModal = () => {
    router.replace(
      {
        pathname: router.pathname,
        query: {
          ...router.query,
          versionFileId: versionFile?._id.toString(),
        },
      },
      undefined,
      { shallow: true }
    );
  };

  return (
    <Tr _hover={{ bg: 'gray.50' }} height="4em">
      {canEditGroupLibrary && (
        <Td text-align="center">
          <Checkbox
            isChecked={
              (selection.allSelected &&
                !selection.excludedItems
                  .map((item) => item._id)
                  .includes(versionFile._id)) ||
              (!selection.allSelected &&
                selection.includedItems
                  .map((item) => item._id)
                  .includes(versionFile._id))
            }
            onChange={() => toggleOne(versionFile)}
            boxSize={6}
          />
        </Td>
      )}
      <Td maxWidth="1px" width="full">
        {router.query.versionFileId === versionFile._id && (
          <PreviewRendererModal
            versionFile={versionFile}
            onClose={() => {
              const { versionFileId, ...query } = router.query;
              router.replace(
                { pathname: router.pathname, query: query },
                undefined,
                { shallow: true }
              );
            }}
            isOpen={router.query.versionFileId === versionFile._id}
            linkOriginalPost
          />
        )}

        <DeleteLibraryResourceModal
          onClose={deleteFileModal.onClose}
          isOpen={deleteFileModal.isOpen}
          groupId={groupId}
          versionFileIds={[versionFile?._id as string]}
          onAfterDeleteFiles={onAfterDelete}
        />

        <BulkMoveLibraryFilesModal
          groupId={groupId}
          versionFileIds={[versionFile?._id as string]}
          open={moveFileModal.isOpen}
          onClose={moveFileModal.onClose}
          rootFolderId={rootFolderId}
          onAfterMove={onAfterMove}
        />

        <RenameLibraryResourceModal
          isOpen={renameFileModal.isOpen}
          onClose={renameFileModal.onClose}
          versionFileId={versionFile._id}
          initialName={versionFile.file.name}
        />
        <Tooltip
          hasArrow
          label={
            <FileNameTooltipLabel
              previewDisabled={!!previewDisabled}
              fileName={versionFile.file.name}
              fileNameLengthThreshold={
                !!lineLength && !!numLines ? lineLength * numLines : 0
              }
            />
          }
          isDisabled={
            !previewDisabled &&
            !!lineLength &&
            !!numLines &&
            versionFile.file.name.length <= lineLength * numLines
          }
        >
          <HStack
            _hover={!!previewDisabled ? {} : { cursor: 'pointer' }}
            onClick={!!previewDisabled ? () => {} : openFilePreviewModal}
          >
            <ChakraIcon boxSize={6} />
            <TruncatedItemName
              name={versionFile.file.name}
              minEndChars={8}
              numOfLines={numLines}
              maxLineLength={lineLength}
              textStyle="compactStrong"
            />
          </HStack>
        </Tooltip>
      </Td>
      <Hide below="lg">
        <Td>
          <Text textStyle="compact">
            {dayjs(versionFile.dateCreated).format('MMM D, YYYY')}
          </Text>
        </Td>
        <Td>
          <Tooltip label={versionFile.author.displayName} hasArrow>
            <Box display="inline-block" pl="2">
              <NextLink href={`/members/${versionFile.author._id}`}>
                <Avatar
                  size="sm"
                  name={versionFile.author.displayName}
                  src={versionFile.author.proxyProfilePictureUrl || undefined}
                />
              </NextLink>
            </Box>
          </Tooltip>
        </Td>
      </Hide>
      <Td>
        <FileItemDropdown
          versionFile={versionFile}
          onOpenDeleteFile={deleteFileModal.onOpen}
          onOpenMoveFile={moveFileModal.onOpen}
          onOpenRenameFile={renameFileModal.onOpen}
          onOpenPreviewFile={openFilePreviewModal}
          previewDisabled={!!previewDisabled}
          canEditGroupLibrary={canEditGroupLibrary}
        />
      </Td>
    </Tr>
  );
};

ResourceListItem.versionFileFragment = gql`
  fragment ResourceListItem_VersionFileFragment on VersionFile {
    _id
    dateCreated
    originalPost {
      _id
    }
    originalComment {
      _id
    }
    author {
      _id
      displayName
      proxyProfilePictureUrl(
        input: { w: 40, h: 40, fit: "facearea", facepad: 10.0 }
      )
    }
    file {
      _id
      name
      url
      type
    }
    ...PreviewRenderer_VersionFileFragment
    ...FileItemDropdown_VersionFileFragment
  }
  ${PREVIEW_RENDERER_VERSION_FILE_FRAGMENT}
  ${FileItemDropdown.versionFileFragment}
`;
